import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { CardCategoryId, FlightAlertRow, ProjectId, SquadId } from '@voll-intelligence/domain';
import { HttpError } from '@voll-intelligence/domain';

import { queryKeys } from '@web/core/query';
import {
  flightAlertsRepository,
  projectsRepository,
  useCreateCardPrefs,
} from '@web/data/repositories';
import { t } from '@web/i18n';
import {
  Button,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  useOrgContext,
  useSnackbar,
} from '@web/ui/core';
import { Drawer } from '@web/ui/core/organisms';
import { fireAndForget } from '@web/ui/core/utils';

interface Props {
  row: FlightAlertRow;
  onClose: () => void;
  onCreated: (cardId: string, squadId: string | null) => void;
}

interface Selection {
  projectId: ProjectId;
  squadId: SquadId;
  categoryId: CardCategoryId;
}

interface Picks {
  projectId: ProjectId | null;
  squadId: SquadId | null;
  categoryId: CardCategoryId | null;
}

type IdList<T> = ReadonlyArray<{ id: T }>;

interface OptionLists {
  projects: IdList<ProjectId> | undefined;
  squads: IdList<SquadId>;
  categories: IdList<CardCategoryId> | undefined;
}

/**
 * `null` unless every id is picked AND still a member of the currently
 * loaded option lists — the confirm button's `ready` gate, and what
 * actually gets posted (`useMutation` reads it from `mutate`'s argument,
 * never from a non-null-asserted closure). Membership matters because the
 * initial ids can come straight from remembered prefs (`useCreateCardPrefs`,
 * not org-scoped): a project/squad/category chosen in a different org, or
 * since removed, must not be postable just because it's non-null — the
 * `useReconcileProject`/`useDropIfMissing` effects below correct the STATE
 * for that case, but this check is independent so a stale id can never slip
 * through on the render before those effects run.
 */
function selectionOf(picks: Picks, options: OptionLists): Selection | null {
  const { projectId, squadId, categoryId } = picks;
  if (projectId === null || squadId === null || categoryId === null) return null;
  if (!options.projects?.some((p) => p.id === projectId)) return null;
  if (!options.squads.some((s) => s.id === squadId)) return null;
  if (!options.categories?.some((c) => c.id === categoryId)) return null;
  return { projectId, squadId, categoryId };
}

/** Clears `value` once `options` has loaded and no longer contains it — a
 *  no-op while `options` is still `undefined` (query in flight), so a
 *  remembered id is never cleared just because the network hasn't answered
 *  yet. Shared by the squad and category reconciliation below. */
function useDropIfMissing<T>(
  value: T | null,
  options: IdList<T> | undefined,
  setValue: (v: T | null) => void,
): void {
  useEffect(() => {
    if (!options) return;
    if (value !== null && !options.some((o) => o.id === value)) setValue(null);
  }, [options, value, setValue]);
}

/** Drops a stale project id once the list is loaded (remembered prefs are
 *  not org-scoped, and a project can be deleted after being remembered),
 *  then lets a single-project org auto-select its sole project either way. */
function useReconcileProject(
  projectId: ProjectId | null,
  projects: IdList<ProjectId> | undefined,
  setProjectId: (v: ProjectId | null) => void,
): void {
  useEffect(() => {
    if (!projects) return;
    const stillValid = projectId !== null && projects.some((p) => p.id === projectId);
    if (stillValid) return;
    const sole = projects.length === 1 ? projects[0] : undefined;
    setProjectId(sole?.id ?? null);
  }, [projects, projectId, setProjectId]);
}

/** `HttpError.details.error` — the kanban-api error code, when there is one. */
function errorCode(error: unknown): string | undefined {
  if (!(error instanceof HttpError)) return undefined;
  return (error.details as { error?: string } | undefined)?.error;
}

/** A 404 here is a normal outcome, not a bug: the alerts aggregate is up to
 *  15s stale (see `flight_alerts_repository.ts`). Shared by the copy lookup
 *  and the query invalidation so both fire on exactly the same condition. */
function isAlertNotFound(error: unknown): boolean {
  return (
    error instanceof HttpError && error.status === 404 && errorCode(error) === 'alert_not_found'
  );
}

/** The traveler couldn't be resolved server-side — also a normal, retryable
 *  outcome rather than a bug. */
function isCustomerUnavailable(error: unknown): boolean {
  return (
    error instanceof HttpError &&
    error.status === 503 &&
    errorCode(error) === 'customer_unavailable'
  );
}

/** Maps a failed create-card attempt to its copy key; everything not
 *  recognized falls back to the generic error. */
function errorCopyKey(error: unknown): Parameters<typeof t>[0] {
  if (isAlertNotFound(error)) return 'alerts.create.stale';
  if (isCustomerUnavailable(error)) return 'alerts.create.customerUnavailable';
  return 'alerts.create.error';
}

/** True once either option query has failed terminally. `GlobalErrorHandler`
 *  has no snackbar for a generic 404/500 on these endpoints, so the sheet
 *  has to say so itself (and `selectionOf` above already keeps `ready`
 *  false, since a failed query's `data` stays `undefined`). */
function hasOptionsError(...queries: ReadonlyArray<{ isError: boolean }>): boolean {
  return queries.some((q) => q.isError);
}

/** Retries whichever of the two option queries is currently in error (both,
 *  if both are) — the sheet's manual recovery action for the case above. */
function retryFailedOptions(
  ...queries: ReadonlyArray<{ isError: boolean; refetch: () => Promise<unknown> }>
): void {
  for (const q of queries) {
    if (q.isError) fireAndForget(q.refetch());
  }
}

/** The options-error notice + retry action, or nothing. A standalone
 *  component (rather than an inline ternary) so its branch doesn't count
 *  against `CreateCardSheet`'s own complexity. */
function OptionsErrorNotice({
  show,
  onRetry,
}: Readonly<{ show: boolean; onRetry: () => void }>): React.JSX.Element | null {
  if (!show) return null;
  return (
    <div className="flex flex-col items-start gap-3">
      <p className="m-0 text-meta text-tone-red-fg">{t('alerts.create.optionsError')}</p>
      <Button variant="outline" size="sm" onClick={onRetry}>
        {t('alerts.retry')}
      </Button>
    </div>
  );
}

/**
 * "Criar card" sheet, opened from a flight-alert row that has no card yet.
 * Squad/category picking mirrors the transfer dialogs (`transfer_menu.tsx`):
 * projects already carry their squads embedded (`ProjectWithPermissions.squads`),
 * so there's no separate squads fetch — a project Select only appears when the
 * org has more than one project, otherwise the sole project is auto-selected.
 */
export function CreateCardSheet({ row, onClose, onCreated }: Readonly<Props>): React.JSX.Element {
  const { orgId } = useOrgContext();
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbar();
  const prefs = useCreateCardPrefs();

  // Both option queries render their own inline error + retry
  // (`OptionsErrorNotice` below via `hasOptionsError`/`retryFailedOptions`) —
  // `GlobalErrorHandler` must not ALSO toast a global error for them.
  const projects = useQuery({
    queryKey: queryKeys.projects.list(orgId),
    queryFn: () => projectsRepository.loadProjects(orgId),
    meta: { silent: true },
  });
  const categories = useQuery({
    queryKey: queryKeys.alerts.categories(orgId),
    queryFn: () => flightAlertsRepository.listCardCategories(orgId),
    meta: { silent: true },
  });

  const [projectId, setProjectId] = useState<ProjectId | null>(prefs.projectId as ProjectId | null);
  const [squadId, setSquadId] = useState<SquadId | null>(prefs.squadId as SquadId | null);
  const [categoryId, setCategoryId] = useState<CardCategoryId | null>(
    prefs.categoryId as CardCategoryId | null,
  );

  useReconcileProject(projectId, projects.data, setProjectId);
  const squadOptions = useMemo(
    () => projects.data?.find((p) => p.id === projectId)?.squads ?? [],
    [projects.data, projectId],
  );
  useDropIfMissing(squadId, projects.data ? squadOptions : undefined, setSquadId);
  useDropIfMissing(categoryId, categories.data, setCategoryId);

  const create = useMutation({
    mutationFn: (vars: Selection) => flightAlertsRepository.createCard(orgId, row.alertKey, vars),
    onSuccess: (res, vars) => {
      prefs.remember(vars);
      // The row this sheet came from now HAS a card; drop the aggregate so
      // the list behind the sheet stops offering "Criar card" for it.
      fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all }));
      if (!res.created) showSnackbar({ severity: 'info', message: t('alerts.create.existing') });
      onCreated(res.cardId, res.squadId);
    },
    onError: (error) => {
      if (isAlertNotFound(error)) {
        fireAndForget(queryClient.invalidateQueries({ queryKey: queryKeys.alerts.all }));
      }
    },
    // The sheet already renders its own inline error via `errorCopyKey`
    // below (400/404/503 all map to specific copy) — no global toast on top.
    meta: { silent: true },
  });

  const selection = selectionOf(
    { projectId, squadId, categoryId },
    { projects: projects.data, squads: squadOptions, categories: categories.data },
  );
  const ready = selection !== null && !create.isPending;
  const travelerName = row.traveler.name ?? row.traveler.customerId;

  const optionsError = hasOptionsError(projects, categories);
  const retryOptions = (): void => {
    retryFailedOptions(projects, categories);
  };

  return (
    <Drawer open onClose={onClose} ariaLabel={t('alerts.create.title')}>
      <div className="flex flex-col gap-4 p-5">
        <h2 className="m-0 font-serif text-drawer-title font-medium">{t('alerts.create.title')}</h2>
        <p className="m-0 text-meta text-muted-foreground">
          {t('alerts.create.intro', { name: travelerName })}
        </p>

        <OptionsErrorNotice show={optionsError} onRetry={retryOptions} />

        {(projects.data?.length ?? 0) > 1 ? (
          <label className="flex flex-col gap-1 text-meta">
            {t('alerts.create.project')}
            <Select
              value={projectId ?? undefined}
              onValueChange={(v) => {
                setProjectId(v as ProjectId);
                setSquadId(null);
              }}
            >
              <SelectTrigger aria-label={t('alerts.create.project')}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(projects.data ?? []).map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
        ) : null}

        <label className="flex flex-col gap-1 text-meta">
          {t('alerts.create.squad')}
          <Select
            value={squadId ?? undefined}
            onValueChange={(v) => {
              setSquadId(v as SquadId);
            }}
          >
            <SelectTrigger aria-label={t('alerts.create.squad')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {squadOptions.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          {projectId !== null && squadOptions.length === 0 ? (
            <span className="text-card-id text-muted-foreground">
              {t('alerts.create.noSquads')}
            </span>
          ) : null}
        </label>

        <label className="flex flex-col gap-1 text-meta">
          {t('alerts.create.category')}
          <Select
            value={categoryId ?? undefined}
            onValueChange={(v) => {
              setCategoryId(v as CardCategoryId);
            }}
          >
            <SelectTrigger aria-label={t('alerts.create.category')}>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(categories.data ?? []).map((c) => (
                <SelectItem key={c.id} value={c.id}>
                  {c.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </label>

        {create.isError ? (
          <p className="m-0 text-meta text-tone-red-fg">{t(errorCopyKey(create.error))}</p>
        ) : null}

        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={onClose}>
            {t('alerts.create.cancel')}
          </Button>
          <Button
            disabled={!ready}
            onClick={() => {
              if (selection) create.mutate(selection);
            }}
          >
            {t('alerts.create.confirm')}
          </Button>
        </div>
      </div>
    </Drawer>
  );
}
